import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { NAV } from "@/constants/testIds/site";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavLink = ({ href, testId, children, onClick }) => (
  <a
    href={href}
    data-testid={testId}
    onClick={onClick}
    className="relative text-sm tracking-wide text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors duration-300 group"
  >
    {children}
    <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#8B1A1A] transition-all duration-300 group-hover:w-full" />
  </a>
);

export default function Nav() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F7F6F2]/85 border-b border-[#1A1A1A]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid={NAV.brand}
          onClick={scrollTo("top")}
          aria-label="Decrepalma"
          className="flex items-center gap-3 group"
        >
          <div className="relative h-14 w-14 overflow-hidden">
            <img
              src="/logo.png"
              alt="Decrepalma"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <span className="hidden sm:inline-block font-serif text-2xl tracking-[0.15em] text-[#1A1A1A] uppercase">
            Decrepalma
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink href="#services" testId={NAV.linkServices} onClick={scrollTo("services")}>
            {t.nav.services}
          </NavLink>
          <NavLink href="#about" testId={NAV.linkAbout} onClick={scrollTo("about")}>
            {t.nav.about}
          </NavLink>
          <NavLink href="#contact" testId={NAV.linkContact} onClick={scrollTo("contact")}>
            {t.nav.contact}
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={NAV.langSwitcher}
                aria-label={t.nav.langLabel}
                className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#1A1A1A] hover:text-[#8B1A1A] transition-colors px-3 py-2 border border-[#1A1A1A]/10 hover:border-[#8B1A1A]/40"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-[#1A1A1A]/10 min-w-[140px]">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={NAV.langOption(l.code)}
                  onClick={() => setLang(l.code)}
                  className={`text-xs uppercase tracking-[0.2em] cursor-pointer rounded-none ${
                    lang === l.code ? "text-[#8B1A1A]" : "text-[#1A1A1A]"
                  }`}
                >
                  {l.label} · {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href="#contact"
            data-testid={NAV.ctaBook}
            onClick={scrollTo("contact")}
            className="hidden md:inline-flex items-center bg-[#8B1A1A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#1A1A1A] transition-colors duration-300"
          >
            {t.nav.cta}
          </a>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-[#1A1A1A]"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-[#F7F6F2] border-t border-[#1A1A1A]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <a href="#services" onClick={scrollTo("services")} className="text-[#1A1A1A] text-lg font-serif">
                {t.nav.services}
              </a>
              <a href="#about" onClick={scrollTo("about")} className="text-[#1A1A1A] text-lg font-serif">
                {t.nav.about}
              </a>
              <a href="#contact" onClick={scrollTo("contact")} className="text-[#1A1A1A] text-lg font-serif">
                {t.nav.contact}
              </a>
              <div className="flex gap-2 pt-2">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`text-xs uppercase tracking-[0.2em] px-3 py-2 border ${
                      lang === l.code
                        ? "border-[#8B1A1A] text-[#8B1A1A]"
                        : "border-[#1A1A1A]/10 text-[#1A1A1A]"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a
                href="#contact"
                onClick={scrollTo("contact")}
                className="inline-flex items-center justify-center bg-[#8B1A1A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 mt-2"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
